import { useEffect, useState } from 'react';
import { endpoints } from '../../api';
import { uiText, useAccountLocale } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import type { UserAvatar } from '../../api/people';
import type { PhotoPolicy } from './photo-model';
import { StudioPeopleRow } from '../studio/StudioPeopleRow';
import { useStudioArtwork } from '../studio/use-studio-artwork';
import { TextEditor } from '../../ui/TextEditor';
import { artworkUrl } from '../studio/studio-art-model';
import { StudioIcon } from '../studio/StudioIcon';
import { PromptChip } from '../../ui/PromptChip';
interface Choice {
  value: string;
  name: string;
  disabled: boolean;
  image?: string | null;
}
/** Все слоты людей используют одну пакетную загрузку каталога, а не GET на каждый слот. */
export function PhotoPeople({ state, policy, avatar }: { state: CircleFormState; policy: PhotoPolicy; avatar: UserAvatar | null }) {
  return policy.people_available ? <PhotoPeopleFields state={state} policy={policy} avatar={avatar} /> : null;
}
function PhotoPeopleFields({ state, policy, avatar }: { state: CircleFormState; policy: PhotoPolicy; avatar: UserAvatar | null }) {
  const [open, setOpen] = useState(false),
    [ownChoices, setChoices] = useState<Choice[] | null>(null),
    [error, setError] = useState(false),
    [attempt, retry] = useState(0);
  const [editing, setEditing] = useState<number | null>(null);
  const locale = useAccountLocale() === 'ru' ? 'ru' : 'en';
  const artwork = useStudioArtwork(locale, attempt);
  const choices =
    ownChoices && artwork.data
      ? [
          ...ownChoices,
          ...artwork.data.avatars.map(person => ({
            value: `character:${person.id}`,
            name: person.full_name || person.name,
            disabled: false,
            image: person.image_url,
          })),
        ]
      : null;
  const loadError = error || (artwork.error && !artwork.data);
  useEffect(() => {
    let active = true;
    const request = new AbortController();
    void Promise.all([endpoints.people.self(), endpoints.people.list()])
      .then(([self, people]) => {
        if (!active) return;
        setChoices([
          ...(self.ready ? [{ value: 'self', name: uiText('Вы'), disabled: false, image: self.photos?.[0]?.url }] : []),
          ...people.items.map(person => ({
            value: `person:${person.id}`,
            name: person.name,
            disabled: !person.selectable,
            image: person.photos?.[0]?.url,
          })),
        ]);
        setError(false);
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
      request.abort();
    };
  }, [locale, attempt]);
  const selected = Array.from({ length: policy.max_people }, (_, i) => state.inputs[`cast_${i + 1}`]).filter(Boolean);
  const allChoices = [
    ...(avatar?.status === 'ready'
      ? [{ value: `user_avatar:${avatar.id}`, name: avatar.variant_name, image: avatar.image_url, disabled: false }]
      : []),
    ...(choices ?? []),
  ];
  const toggle = (value: string) =>
    state.setInputs(current => {
      const next = { ...current };
      const values = Array.from({ length: policy.max_people }, (_, i) => current[`cast_${i + 1}`]);
      const existing = values.indexOf(value);
      if (existing >= 0) {
        delete next[`cast_${existing + 1}`];
        delete next[`action_${existing + 1}`];
      } else {
        const free = values.findIndex(item => !item);
        if (free < 0) return current;
        next[`cast_${free + 1}`] = value;
      }
      return next;
    });
  return (
    <section className="photo-people">
      <div className="photo-people-heading">
        <span>
          {uiText('Кто в кадре')} · {selected.length ? `${selected.length} ${uiText('из')} ${policy.max_people}` : uiText('необязательно')}
        </span>
        <button type="button" className="btn line" disabled={state.locked || !choices || loadError} onClick={() => setOpen(true)}>
          <StudioIcon kind="edit" />
          {uiText('Изменить')}
        </button>
      </div>
      {!choices && !loadError && <p role="status">{uiText('Загружаем…')}</p>}
      {loadError && (
        <p role="alert">
          {uiText('Не удалось загрузить людей и персонажей.')}{' '}
          <button type="button" onClick={() => retry(value => value + 1)}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      <div className="photo-people-sub">
        <span>{uiText('Вы, близкие и персонажи')}</span>
        <button type="button" disabled={state.locked || !choices || loadError} onClick={() => setOpen(true)}>
          {uiText('Персонажи')} ›
        </button>
      </div>
      <StudioPeopleRow
        label="Кто в кадре"
        value=""
        selectedValues={selected}
        disabled={state.locked || !choices || loadError}
        open={open}
        onOpenChange={setOpen}
        keepOpen
        compatSelect={false}
        onPick={toggle}
        choices={allChoices.map(choice => ({
          ...choice,
          disabled: !selected.includes(choice.value) && (choice.disabled || selected.length >= policy.max_people),
        }))}
      />
      {!!selected.length && (
        <div className="photo-people-actions">
          <div className="photo-people-sub">
            <span>{uiText('Что делают в кадре')}</span>
            <small>{uiText('необязательно')}</small>
          </div>
          {Array.from({ length: policy.max_people }, (_, i) => i + 1)
            .filter(i => state.inputs[`cast_${i}`])
            .map(i => {
              const choice = allChoices.find(choice => choice.value === state.inputs[`cast_${i}`]);
              return (
                <div key={i} className="photo-person-action ps-actf">
                  <span className="photo-action-person" title={choice?.name} aria-hidden="true">
                    {artworkUrl(choice?.image) ? <img src={artworkUrl(choice?.image)!} alt="" /> : choice?.name.slice(0, 1)}
                  </span>
                  <div
                    className="chips photo-action-chips ps-acts"
                    role="group"
                    aria-label={`${uiText('Что делает в кадре')} · ${choice?.name ?? uiText('Человек')}`}
                  >
                    {['Смотрит в камеру', 'Обнимает меня', 'Улыбается'].map(value => (
                      <PromptChip
                        key={value}
                        label={uiText(value)}
                        selected={state.inputs[`action_${i}`] === value}
                        variant="action"
                        disabled={state.locked}
                        onClick={() =>
                          state.setInputs(current => ({ ...current, [`action_${i}`]: current[`action_${i}`] === value ? '' : value }))
                        }
                      />
                    ))}
                    <button
                      type="button"
                      className={`chip${state.inputs[`action_${i}`] && !['Смотрит в камеру', 'Обнимает меня', 'Улыбается'].includes(state.inputs[`action_${i}`]) ? ' on' : ''}`}
                      disabled={state.locked}
                      onClick={() => setEditing(i)}
                    >
                      {state.inputs[`action_${i}`] &&
                      !['Смотрит в камеру', 'Обнимает меня', 'Улыбается'].includes(state.inputs[`action_${i}`])
                        ? state.inputs[`action_${i}`]
                        : uiText('Своё действие')}
                    </button>
                  </div>
                  {!choice && (
                    <button
                      type="button"
                      className="photo-person-remove"
                      disabled={state.locked}
                      onClick={() => toggle(state.inputs[`cast_${i}`])}
                    >
                      {uiText('Убрать из кадра')}
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      )}
      {editing !== null && (
        <TextEditor
          label={`${uiText('Что делает в кадре')} · ${allChoices.find(choice => choice.value === state.inputs[`cast_${editing}`])?.name ?? uiText('Человек')}`}
          value={state.inputs[`action_${editing}`] ?? ''}
          limit={200}
          disabled={state.locked}
          onChange={value => state.setInputs(current => ({ ...current, [`action_${editing}`]: value }))}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}
